import { Link as InertiaLink, useForm, usePage } from '@inertiajs/react';
import { styled, type Theme } from '@mui/material/styles';
import { posthog } from 'posthog-js';
import { useEffect, useRef, useState } from 'react';

import AppTheme from '@/themes/AppTheme';

import { stringToColor } from '@/helpers/stringToColor';
import useDrawer from '@/hooks/useDrawer';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import MuiAppBar, { AppBarProps } from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiDrawer, { DrawerProps } from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import MenuIcon from '@mui/icons-material/Menu';

import logo_square from '@/../assets/images/logo-sqare.svg';
import AddRing from '@/svgIcons/AddRing';

import AppSearch from '@/components/AppSearch';
import DrawerContent from '@/components/DrawerContent';
import EmailVerificationDialog from '@/components/EmailVerificationDialog';
import Providers from '@/components/Providers';
import { PageProps } from '@/types';

const drawerWidth = 280;
const drawerClosedWidth = 65;

const drawerColor = '#42474d';

const openedMixin = (theme: Theme) => ({
  backgroundColor: drawerColor,
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme) => ({
  backgroundColor: drawerColor,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  // overflowY: 'hidden',
  // padding: theme.spacing(1, 1, 1, 1),
  [theme.breakpoints.up('sm')]: {
    width: `calc(${drawerClosedWidth} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  border: 0,
  color: '#9499A6',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'desktop',
})<AppBarProps & { open: boolean; desktop: boolean }>(
  ({ theme, open, desktop }) => ({
    zIndex: desktop ? theme.zIndex.drawer + 1 : theme.zIndex.drawer - 1,
    backgroundColor: drawerColor,
    border: 0,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: drawerClosedWidth,
    width: `calc(100% - ${drawerClosedWidth}px)`,
    ...(open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
    }),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      width: '100%',
    },
  }),
);

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})<DrawerProps & { open: boolean }>(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  color: 'black !important',
  '& .MuiPaper-root': {
    border: 0,
  },
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  marginTop: theme.spacing(8),
  padding: theme.spacing(3),
  borderTopLeftRadius: theme.shape.borderRadius,
  maxHeight: '100vh',
  overflowY: 'auto',
  backgroundColor: theme.palette.background.default,
}));

function Content({
  children,
  noPadding,
}: {
  children: React.ReactNode;
  noPadding: boolean | undefined;
}) {
  const { auth } = usePage<PageProps>().props;
  const helpAnchorRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const {
    user: { permissions, name, email, id },
  } = auth;

  const { open, setOpen, desktop } = useDrawer();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const helpForm = useForm({
    service: 'Issue Database',
    message: '',
  });

  useEffect(() => {
    console.log('Posthog identify', name, email);
    posthog.identify(String(id), {
      name,
      email,
    });
  }, [name, email, id]);

  // UserGuiding initialization effect removed

  return (
    <>
      <EmailVerificationDialog />
      <Popover
        anchorEl={helpAnchorRef.current}
        open={helpMenuOpen}
        onClose={() => setHelpMenuOpen(false)}
        slotProps={{
          paper: {
            sx: {
              padding: 2,
              width: 450,
              backgroundColor: 'common.white',
            },
            variant: 'elevation',
            elevation: 4,
          },
        }}
      >
        <Typography variant={'h6'} gutterBottom>
          Help Center
        </Typography>
        <Typography variant={'body2'} gutterBottom>
          Please fill out the form below and we will get back to you as soon as
          possible.
        </Typography>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            helpForm.post(route('help-center.store'), {
              onSuccess: () => {
                setHelpMenuOpen(false);
                helpForm.reset();
              },
            });
          }}
        >
          <TextField
            fullWidth
            margin="normal"
            label="Service"
            select
            size="small"
            value={helpForm.data.service}
            onChange={(e) => helpForm.setData('service', e.target.value)}
          >
            {/*
              Setting up FactoryQA
              Using FactoryQA
              ISO 9001 Support
              AS9100 Support
              ITAR Support
              General Quality Support/Questions
          */}
            <MenuItem value="Setting up FactoryQA">
              Setting up FactoryQA
            </MenuItem>
            <MenuItem value="Using FactoryQA">Using FactoryQA</MenuItem>
            <MenuItem value="ISO 9001 Support">ISO 9001 Support</MenuItem>
            <MenuItem value="AS9100 Support">AS9100 Support</MenuItem>
            <MenuItem value="ITAR Support">ITAR Support</MenuItem>
            <MenuItem value="General Quality Support/Questions">
              General Quality Support/Questions
            </MenuItem>
            <MenuItem value="Other">Other...</MenuItem>
          </TextField>

          <TextField
            fullWidth
            margin="normal"
            label="Message"
            multiline
            minRows={4}
            value={helpForm.data.message}
            onChange={(e) => helpForm.setData('message', e.target.value)}
          />

          <Box sx={{ display: 'flex', gap: 1, marginTop: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              component="a"
              href="https://help.factoryqa.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setHelpMenuOpen(false)}
            >
              Help Center
            </Button>
            <Button variant="outlined" color="primary" type="submit">
              Submit
            </Button>
            <Button onClick={() => setHelpMenuOpen(false)}>Cancel</Button>
          </Box>
        </form>
      </Popover>
      <Menu
        slotProps={{ paper: { sx: { minWidth: 400 } } }}
        open={userMenuOpen}
        onClose={() => setUserMenuOpen(false)}
        anchorEl={userMenuRef.current}
      >
        <MenuItem
          divider
          component={InertiaLink}
          href={route('profile.edit', { profile: 'me' })}
        >
          <ListItemAvatar>
            <Avatar
              variant="circular"
              style={{
                width: 50,
                height: 50,
                fontSize: 14,
                backgroundColor: stringToColor(auth.user.name),
              }}
            >
              {auth.user.name
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </Avatar>
          </ListItemAvatar>
          <Box>
            <Typography>
              <strong>{auth.user.name}</strong>
            </Typography>
            <Typography color="grey.500">{auth.user.email}</Typography>
            <Typography color="primary">Profile & Preferences</Typography>
          </Box>
        </MenuItem>
        <MenuItem
          sx={{ textTransform: 'capitalize' }}
          component={InertiaLink}
          href={route('settings.company.setup')}
        >
          Company Setup
        </MenuItem>
        <MenuItem
          divider
          sx={{ textTransform: 'capitalize' }}
          component={InertiaLink}
          href={route('settings.subscription')}
        >
          Billing
        </MenuItem>
        <MenuItem
          sx={{ textTransform: 'capitalize' }}
          component="a"
          href="https://factoryqa.com/pricing"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setUserMenuOpen(false)}
        >
          Pricing
        </MenuItem>
        <MenuItem
          sx={{ textTransform: 'capitalize' }}
          component="a"
          href="https://help.factoryqa.com"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setUserMenuOpen(false)}
        >
          Help Center
        </MenuItem>
        <MenuItem
          sx={{ textTransform: 'capitalize' }}
          onClick={() => {
            setUserMenuOpen(false);
            setHelpMenuOpen(true);
          }}
        >
          Training & Support
        </MenuItem>
        {auth.user.super_user && (
          <MenuItem
            sx={{ textTransform: 'capitalize' }}
            component="a"
            href="/admin"
          >
            Admin Panel
          </MenuItem>
        )}
        <MenuItem component={InertiaLink} href={route('logout')}>
          Logout
        </MenuItem>
        {auth.user.companies?.length && auth.user.companies.length > 1 && (
          <MenuItem
            component={InertiaLink}
            href={route('settings.change-company')}
          >
            Change Company
          </MenuItem>
        )}
      </Menu>
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          bgcolor: drawerColor,
        }}
      >
        <AppBar position={'fixed'} elevation={0} open={open} desktop={desktop}>
          <Toolbar>
            <Box
              flexGrow={1}
              marginRight={1}
              sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}
            >
              <Box width={400}>
                <AppSearch />
              </Box>
            </Box>

            <Stack alignItems="center">
              <Grid
                component={Box}
                paddingRight={2}
                display="flex"
                sx={{
                  marginLeft: 'auto',
                  marginRight: 0,
                }}
              >
                <IconButton
                  color="inherit"
                  size="large"
                  onClick={() => setMobileOpen((o) => !o)}
                  sx={{ display: { xs: 'block', sm: 'none' } }}
                >
                  <MenuIcon />
                </IconButton>
                <Tooltip title="Create Issue">
                  <IconButton
                    disabled={permissions.issue_tracker !== 'edit'}
                    component={InertiaLink}
                    href={'/app/issue-tracker'}
                    color="inherit"
                    size="large"
                  >
                    <AddRing />
                  </IconButton>
                </Tooltip>

                <Box marginRight={2}>
                  <Tooltip title="Help Center">
                    <IconButton
                      ref={helpAnchorRef}
                      color="inherit"
                      onClick={() => setHelpMenuOpen(true)}
                      size="large"
                    >
                      <Icon>help</Icon>
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ textDecoration: 'none' }}
                  component={InertiaLink}
                  color="inherit"
                  ref={userMenuRef}
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <Avatar
                    variant="circular"
                    style={{
                      width: 30,
                      height: 30,
                      fontSize: 14,
                      backgroundColor: stringToColor(auth.user.name),
                    }}
                  >
                    {auth.user.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')}
                  </Avatar>

                  <Box marginLeft={1}>
                    <Typography>{auth.user.name}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Stack>
          </Toolbar>
        </AppBar>

        {desktop ? (
          <Drawer variant="permanent" open={open}>
            <DrawerHeader>
              <Box
                flex={1}
                ml={open ? 2 : 0}
                p={0}
                display="flex"
                alignItems="center"
              >
                <Box
                  component="img"
                  src={logo_square}
                  alt="FactoryQA Logo"
                  sx={(theme) => ({
                    width: theme.spacing(5),
                    height: theme.spacing(5),
                  })}
                />
              </Box>
            </DrawerHeader>
            <DrawerContent onClose={() => setOpen((o) => !o)} open={open} />
          </Drawer>
        ) : (
          <MuiDrawer
            variant="temporary"
            open={mobileOpen}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                backgroundColor: drawerColor,
                border: 0,
              },
            }}
          >
            <DrawerContent
              onClose={() => setMobileOpen((o) => !o)}
              open={true}
              onElementClick={() => setMobileOpen(!mobileOpen)}
            />
          </MuiDrawer>
        )}

        <Main sx={{ padding: noPadding ? '0' : '24px' }}>
          {/* Subscription alert banner */}
          {(auth.subscriptions?.subscription_type === 'free' ||
            auth.subscriptions?.is_free_trial) && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                mt: noPadding ? 2 : 0,
                mx: noPadding ? 2 : 0,
                display: 'flex',
                alignItems: 'center',
                '& .MuiAlert-action': {
                  alignItems: 'center',
                  padding: '0px 0px 0px 16px',
                },
              }}
              action={
                <Button
                  size="small"
                  variant="contained"
                  color="inherit"
                  component={InertiaLink}
                  href={route('settings.subscription')}
                >
                  {auth.subscriptions?.is_free_trial ? 'Subscribe' : 'Upgrade'}
                </Button>
              }
            >
              {auth.subscriptions?.is_free_trial ? (
                <>
                  <AlertTitle>Pro Trial</AlertTitle>
                  You have {auth.subscriptions?.trial_days_remaining} days
                  remaining in your Pro trial. Subscribe to continue using all
                  features.
                </>
              ) : (
                <>
                  <AlertTitle>Free Tier</AlertTitle>
                  You are currently on the free tier. Upgrade to unlock more
                  features and capabilities.
                </>
              )}
            </Alert>
          )}
          {children}
        </Main>
      </Box>
    </>
  );
}

function Layout({
  children,
  noPadding,
}: {
  children: React.ReactNode;
  noPadding?: boolean | undefined;
}) {
  return (
    <AppTheme>
      <Providers>
        <Content noPadding={noPadding}>{children}</Content>
      </Providers>
    </AppTheme>
  );
}

export default Layout;
